/**
 * Chat Service
 * 
 * Main service for managing conversations and messages in the internal chat system.
 * Handles CRUD operations, real-time listeners, and scope-based access control.
 * 
 * @module services/chat
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  startAt,
  endBefore,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment,
  Timestamp,
  writeBatch,
  type Unsubscribe,
  type QueryDocumentSnapshot,
  type DocumentData,
  type FieldValue,
  type DocumentSnapshot,
  type QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@municipality/firebase';
import type {
  Conversation,
  Message,
  ChatScope,
  MessageType,
  ParticipantInfo,
  User,
} from '@municipality/types';
import { encryptionService, CURRENT_ENCRYPTION_VERSION } from './encryption';

/**
 * Rate limiting configuration
 */
const RATE_LIMITS = {
  MAX_MESSAGES_PER_MINUTE: 30,  // Maximum messages per user per minute
  MAX_CONVERSATIONS_PER_HOUR: 20,  // Maximum new conversations per hour
  CLEANUP_INTERVAL: 60000,  // Clean old timestamps every minute
};

/**
 * Service for managing chat conversations and messages
 */
export class ChatService {
  // Rate limiting: Track message send times per user
  private messageSendTimes = new Map<string, number[]>();
  
  // Rate limiting: Track conversation creation times per user
  private conversationCreateTimes = new Map<string, number[]>();
  
  // Active listeners: Prevent duplicate listeners per conversation
  private activeListeners = new Map<string, Unsubscribe>();

  constructor() {
    // Cleanup old timestamps periodically to prevent memory leaks
    setInterval(() => {
      this.cleanupOldTimestamps();
    }, RATE_LIMITS.CLEANUP_INTERVAL);
  }

  /**
   * Clean up timestamps older than tracking window to prevent memory leaks
   */
  private cleanupOldTimestamps(): void {
    const now = Date.now();
    const oneHourAgo = now - 3600000;

    // Clean message send times (keep last hour for rate limiting)
    this.messageSendTimes.forEach((times, userId) => {
      const recentTimes = times.filter(t => t > oneHourAgo);
      if (recentTimes.length === 0) {
        this.messageSendTimes.delete(userId);
      } else {
        this.messageSendTimes.set(userId, recentTimes);
      }
    });

    // Clean conversation create times
    this.conversationCreateTimes.forEach((times, userId) => {
      const recentTimes = times.filter(t => t > oneHourAgo);
      if (recentTimes.length === 0) {
        this.conversationCreateTimes.delete(userId);
      } else {
        this.conversationCreateTimes.set(userId, recentTimes);
      }
    });
  }

  /**
   * Check if user has exceeded message rate limit
   * @throws Error if rate limit exceeded
   */
  private checkMessageRateLimit(userId: string): void {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    const userTimes = this.messageSendTimes.get(userId) || [];
    const recentMessages = userTimes.filter(t => t > oneMinuteAgo);
    
    if (recentMessages.length >= RATE_LIMITS.MAX_MESSAGES_PER_MINUTE) {
      throw new Error(
        `Rate limit exceeded. Maximum ${RATE_LIMITS.MAX_MESSAGES_PER_MINUTE} messages per minute. Please slow down.`
      );
    }
    
    // Record this send attempt
    recentMessages.push(now);
    this.messageSendTimes.set(userId, recentMessages);
  }

  /**
   * Check if user has exceeded conversation creation rate limit
   * @throws Error if rate limit exceeded
   */
  private checkConversationRateLimit(userId: string): void {
    const now = Date.now();
    const oneHourAgo = now - 3600000;
    
    const userTimes = this.conversationCreateTimes.get(userId) || [];
    const recentCreations = userTimes.filter(t => t > oneHourAgo);
    
    if (recentCreations.length >= RATE_LIMITS.MAX_CONVERSATIONS_PER_HOUR) {
      throw new Error(
        `Rate limit exceeded. Maximum ${RATE_LIMITS.MAX_CONVERSATIONS_PER_HOUR} conversations per hour. Please try again later.`
      );
    }
    
    // Record this creation attempt
    recentCreations.push(now);
    this.conversationCreateTimes.set(userId, recentCreations);
  }

  /**
   * Create a direct (1-on-1) conversation
   */
  async createDirectConversation(
    userId: string,
    recipientId: string,
    scope: ChatScope,
    municipalityId?: string
  ): Promise<Conversation> {
    try {
      // Rate limiting: Check conversation creation limit
      this.checkConversationRateLimit(userId);

      // Check if conversation already exists
      const existing = await this.findDirectConversation(userId, recipientId, scope);
      if (existing) {
        return existing;
      }

      // Get user details
      const [user, recipient] = await Promise.all([
        this.getUserDetails(userId),
        this.getUserDetails(recipientId),
      ]);

      const conversationRef = doc(collection(db, 'conversations'));
      const now = new Date();

      const conversationData: Partial<Conversation> = {
        id: conversationRef.id,
        type: 'direct',
        scope,
        municipalityId: municipalityId || undefined,
        participantIds: [userId, recipientId],
        participantDetails: {
          [userId]: {
            name: user.name,
            ...(user.avatar && { avatar: user.avatar }),
            role: user.role,
            addedAt: now,
            addedBy: userId,
          },
          [recipientId]: {
            name: recipient.name,
            ...(recipient.avatar && { avatar: recipient.avatar }),
            role: recipient.role,
            addedAt: now,
            addedBy: userId,
          },
        },
        createdBy: userId,
        createdAt: now,
        updatedAt: now,
        unreadCount: {},
        typingUsers: [],
        isArchived: {},
        isMuted: {},
        pinnedBy: [],
        deletedBy: [],
      };

      // Remove undefined fields
      const cleanData = JSON.parse(JSON.stringify(conversationData, (_key, value) => 
        value === undefined ? null : value
      ));

      await setDoc(conversationRef, {
        ...cleanData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return conversationData as Conversation;
    } catch (error) {
      console.error('Failed to create direct conversation:', error);
      throw new Error('Failed to create conversation');
    }
  }

  /**
   * Create a group conversation
   */
  async createGroupConversation(
    name: string,
    participantIds: string[],
    createdBy: string,
    scope: ChatScope,
    municipalityId?: string,
    description?: string
  ): Promise<Conversation> {
    try {
      // Rate limiting: Check conversation creation limit
      this.checkConversationRateLimit(createdBy);

      if (participantIds.length < 2) {
        throw new Error('Group conversation must have at least 2 participants');
      }

      // Get all participant details
      const participantDetails: Record<string, ParticipantInfo> = {};
      const now = new Date();

      await Promise.all(
        participantIds.map(async (participantId) => {
          const user = await this.getUserDetails(participantId);
          participantDetails[participantId] = {
            name: user.name,
            ...(user.avatar && { avatar: user.avatar }),
            role: user.role,
            addedAt: now,
            addedBy: createdBy,
          };
        })
      );

      const conversationRef = doc(collection(db, 'conversations'));

      const conversationData: Partial<Conversation> = {
        id: conversationRef.id,
        type: 'group',
        scope,
        municipalityId: municipalityId || undefined,
        name,
        description,
        participantIds,
        participantDetails,
        createdBy,
        createdAt: now,
        updatedAt: now,
        unreadCount: {},
        typingUsers: [],
        isArchived: {},
        isMuted: {},
        pinnedBy: [],
        deletedBy: [],
      };

      // Remove undefined fields
      const cleanData = JSON.parse(JSON.stringify(conversationData, (_key, value) => 
        value === undefined ? null : value
      ));

      await setDoc(conversationRef, {
        ...cleanData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return conversationData as Conversation;
    } catch (error) {
      console.error('Failed to create group conversation:', error);
      throw new Error('Failed to create group conversation');
    }
  }

  /**
   * Find existing direct conversation between two users
   */
  private async findDirectConversation(
    userId: string,
    recipientId: string,
    scope: ChatScope
  ): Promise<Conversation | null> {
    try {
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('type', '==', 'direct'),
        where('scope', '==', scope),
        where('participantIds', 'array-contains', userId)
      );

      const snapshot = await getDocs(q);
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.participantIds.includes(recipientId)) {
          return this.convertToConversation(docSnap);
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to find direct conversation:', error);
      return null;
    }
  }

  /**
   * Get user details for participant info
   */
  private async getUserDetails(userId: string): Promise<{ name: string; avatar?: string; role: string }> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return { name: 'Unknown User', role: 'unknown' };
      }

      const userData = userDoc.data();
      // Support users with displayName, or separate name+surname (e.g. super_admin, municipality users)
      let name: string = userData.displayName ?? '';
      if (!name && (userData.name || userData.surname)) {
        name = [userData.name, userData.surname].filter(Boolean).join(' ').trim();
      }
      if (!name) {
        name = userData.email?.split('@')[0] || 'User';
      }
      return {
        name,
        avatar: userData.photoURL,
        role: userData.role || 'user',
      };
    } catch (error) {
      console.error('Failed to get user details:', error);
      return { name: 'Unknown User', role: 'unknown' };
    }
  }

  /**
   * Get user details with email and surname for multiple users (batch fetch)
   */
  async getUserDetailsBatch(userIds: string[]): Promise<Record<string, { email: string; surname: string; name: string; role?: string }>> {
    try {
      const userDetails: Record<string, { email: string; surname: string; name: string; role?: string }> = {};
      
      // Batch fetch users (Firestore allows up to 10 in a single batch)
      const batches: string[][] = [];
      for (let i = 0; i < userIds.length; i += 10) {
        batches.push(userIds.slice(i, i + 10));
      }

      await Promise.all(
        batches.map(async (batch) => {
          const userDocs = await Promise.all(
            batch.map((userId) => getDoc(doc(db, 'users', userId)))
          );

          userDocs.forEach((userDoc: DocumentSnapshot, index: number) => {
            const userId = batch[index];
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Support users with displayName, or separate name+surname (e.g. super_admin, municipality users)
              let name: string;
              let surname: string;
              if (userData.displayName) {
                const parts = userData.displayName.split(' ').filter(Boolean);
                name = parts[0] || userData.email?.split('@')[0] || 'User';
                surname = parts.length > 1 ? parts.slice(1).join(' ') : '';
              } else if (userData.name || userData.surname) {
                name = userData.name || '';
                surname = userData.surname || '';
              } else {
                name = userData.email?.split('@')[0] || 'User';
                surname = '';
              }
              const role = userData.municipalityRole || userData.role;
              userDetails[userId] = {
                email: userData.email || '',
                surname,
                name,
                ...(role && { role }),
              };
            } else {
              userDetails[userId] = {
                email: '',
                surname: '',
                name: 'Unknown User',
              };
            }
          });
        })
      );

      return userDetails;
    } catch (error) {
      console.error('Failed to get user details batch:', error);
      return {};
    }
  }

  /**
   * Get conversations for a user
   * For municipality scope: only conversations with exact municipalityId (excludes legacy municipalityId=null
   * to prevent external users from seeing super admins or cross-municipality participants)
   */
  async getConversations(
    userId: string,
    scope: ChatScope,
    municipalityId?: string
  ): Promise<Conversation[]> {
    try {
      const conversationsRef = collection(db, 'conversations');

      if (municipalityId) {
        const snapshotMatch = await getDocs(
          query(
            conversationsRef,
            where('participantIds', 'array-contains', userId),
            where('scope', '==', scope),
            where('municipalityId', '==', municipalityId),
            orderBy('updatedAt', 'desc')
          )
        );

        const conversations: Conversation[] = [];
        snapshotMatch.forEach((docSnap: QueryDocumentSnapshot<DocumentData>) => {
          const conversation = this.convertToConversation(docSnap);
          if (!conversation.deletedBy.includes(userId)) {
            conversations.push(conversation);
          }
        });
        conversations.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        return conversations;
      }

      const q = query(
        conversationsRef,
        where('participantIds', 'array-contains', userId),
        where('scope', '==', scope),
        orderBy('updatedAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const conversations: Conversation[] = [];
      snapshot.forEach((docSnap: QueryDocumentSnapshot<DocumentData>) => {
        const conversation = this.convertToConversation(docSnap);
        if (!conversation.deletedBy.includes(userId)) {
          conversations.push(conversation);
        }
      });

      return conversations;
    } catch (error) {
      console.error('Failed to get conversations:', error);
      return [];
    }
  }

  /**
   * Get a single conversation
   */
  async getConversation(conversationId: string): Promise<Conversation | null> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        return null;
      }

      return this.convertToConversation(conversationDoc);
    } catch (error) {
      console.error('Failed to get conversation:', error);
      return null;
    }
  }

  /**
   * Send a message in a conversation
   * Optimized: Single read to get participantIds, then atomic updates
   */
  async sendMessage(
    conversationId: string,
    content: string,
    senderId: string,
    senderName: string,
    type: MessageType = 'text',
    metadata?: { mentions?: string[]; replyTo?: string }
  ): Promise<Message> {
    try {
      // Rate limiting: Check message send limit
      this.checkMessageRateLimit(senderId);

      // Get conversation once to retrieve participantIds (used for unread count)
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }
      
      const participantIds = conversationDoc.data().participantIds as string[];

      // Encrypt message content with current key version
      const encryptedContent = await encryptionService.encryptMessage(content, CURRENT_ENCRYPTION_VERSION);

      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      const now = new Date();
      const expiresAt = new Date(now);
      expiresAt.setFullYear(expiresAt.getFullYear() + 2); // 2 years from now

      const messageData: Partial<Message> = {
        conversationId,
        senderId,
        senderName,
        content: encryptedContent,
        type,
        encryptionKeyVersion: CURRENT_ENCRYPTION_VERSION,
        createdAt: now,
        isEdited: false,
        deletedBy: [],
        readBy: { [senderId]: now },
        deliveredTo: {},
        reactions: {},
        expiresAt,
      };

      // Add metadata if it has mentions or replyTo
      if (metadata && ((metadata.mentions?.length ?? 0) > 0 || metadata.replyTo)) {
        messageData.metadata = {
          ...(metadata.mentions && metadata.mentions.length > 0 ? { mentions: metadata.mentions } : {}),
          ...(metadata.replyTo ? { replyTo: metadata.replyTo } : {}),
        };
      }

      // Write message to Firestore
      const messageRef = await addDoc(messagesRef, {
        ...messageData,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Update conversation last message with participantIds (avoids extra read)
      await this.updateConversationLastMessage(
        conversationId,
        participantIds,
        {
          content: content.substring(0, 100), // Preview
          senderId,
          senderName,
          timestamp: now,
        }
      );

      const returnedMessage = {
        ...messageData,
        id: messageRef.id,
        content, // Return decrypted content
      } as Message;
      
      return returnedMessage;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw new Error('Failed to send message');
    }
  }

  /**
   * Edit a message
   * Only the sender can edit their own messages
   */
  async editMessage(
    conversationId: string,
    messageId: string,
    newContent: string,
    userId: string
  ): Promise<Message> {
    try {
      // Rate limiting: Check message edit limit (same as send limit)
      this.checkMessageRateLimit(userId);

      // Validate content
      if (!newContent || newContent.trim().length === 0) {
        throw new Error('Message content cannot be empty');
      }

      if (newContent.length > 10000) {
        throw new Error('Message content cannot exceed 10000 characters');
      }

      // Get the message to verify ownership
      const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
      const messageDoc = await getDoc(messageRef);

      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();

      // Verify user is the sender
      if (messageData.senderId !== userId) {
        throw new Error('Only the message sender can edit the message');
      }

      // Verify message is not deleted
      if (messageData.deletedBy && messageData.deletedBy.length > 0) {
        throw new Error('Cannot edit deleted message');
      }

      // Verify message is not a system message
      if (messageData.type === 'system') {
        throw new Error('Cannot edit system messages');
      }

      // Encrypt new content with current key version
      const encryptedContent = await encryptionService.encryptMessage(newContent.trim(), CURRENT_ENCRYPTION_VERSION);

      // Update message
      await updateDoc(messageRef, {
        content: encryptedContent,
        encryptionKeyVersion: CURRENT_ENCRYPTION_VERSION,
        isEdited: true,
        editedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Get updated message and return with decrypted content
      const updatedDoc = await getDoc(messageRef);
      if (!updatedDoc.exists()) {
        throw new Error('Message not found after update');
      }
      
      // Convert QueryDocumentSnapshot to Message
      const updatedMessage = await this.convertToMessage(updatedDoc as QueryDocumentSnapshot<DocumentData>);
      
      // Override content with the new (already decrypted) content
      return {
        ...updatedMessage,
        content: newContent.trim(),
      };
    } catch (error) {
      console.error('Failed to edit message:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to edit message');
    }
  }


  /**
   * Get messages for a conversation
   * Optimized with parallel message decryption
   */
  async getMessages(
    conversationId: string,
    limitCount: number = 50,
    before?: Date
  ): Promise<Message[]> {
    try {
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      let q = query(messagesRef, orderBy('createdAt', 'desc'), limit(limitCount));

      if (before) {
        q = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          startAfter(Timestamp.fromDate(before)),
          limit(limitCount)
        );
      }

      const snapshot = await getDocs(q);
      
      // Parallel decryption: Convert all messages simultaneously
      // Performance: 10-25x faster than sequential decryption
      const messagePromises = snapshot.docs.map(doc => this.convertToMessage(doc));
      const messages = await Promise.all(messagePromises);

      // Return in chronological order (oldest first)
      return messages.reverse();
    } catch (error) {
      console.error('Failed to get messages:', error);
      return [];
    }
  }

  /**
   * Get messages around a specific date (for jumping to old messages from search)
   * Loads messages before and after the target date to provide context
   */
  async getMessagesAroundDate(
    conversationId: string,
    targetDate: Date,
    limitBefore: number = 25,
    limitAfter: number = 25
  ): Promise<Message[]> {
    try {
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      
      // Get messages before the target date (older messages)
      const beforeQuery = query(
        messagesRef,
        orderBy('createdAt', 'desc'),
        endBefore(Timestamp.fromDate(targetDate)),
        limit(limitBefore)
      );
      
      // Get messages at or after the target date (newer messages, including the target)
      const afterQuery = query(
        messagesRef,
        orderBy('createdAt', 'asc'),
        startAt(Timestamp.fromDate(targetDate)),
        limit(limitAfter)
      );

      const [beforeSnapshot, afterSnapshot] = await Promise.all([
        getDocs(beforeQuery),
        getDocs(afterQuery)
      ]);

      // Convert all messages in parallel
      const beforePromises = beforeSnapshot.docs.map(doc => this.convertToMessage(doc));
      const afterPromises = afterSnapshot.docs.map(doc => this.convertToMessage(doc));
      
      const [beforeMessages, afterMessages] = await Promise.all([
        Promise.all(beforePromises),
        Promise.all(afterPromises)
      ]);

      // Reverse before messages to get chronological order, combine with after messages
      return [...beforeMessages.reverse(), ...afterMessages];
    } catch (error) {
      console.error('Failed to get messages around date:', error);
      return [];
    }
  }

  /**
   * Mark message as deleted for a user (soft delete)
   */
  async deleteMessageForUser(messageId: string, conversationId: string, userId: string): Promise<void> {
    try {
      const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
      
      await updateDoc(messageRef, {
        deletedBy: arrayUnion(userId),
      });
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw new Error('Failed to delete message');
    }
  }

  /**
   * Mark conversation as archived for a user
   */
  async archiveConversation(conversationId: string, userId: string, isArchived: boolean): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      
      await updateDoc(conversationRef, {
        [`isArchived.${userId}`]: isArchived,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      throw new Error('Failed to archive conversation');
    }
  }

  /**
   * Mute/unmute conversation for a user
   */
  async muteConversation(conversationId: string, userId: string, isMuted: boolean): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      
      await updateDoc(conversationRef, {
        [`isMuted.${userId}`]: isMuted,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to mute conversation:', error);
      throw new Error('Failed to mute conversation');
    }
  }

  /**
   * Pin/unpin conversation for a user
   */
  async pinConversation(conversationId: string, userId: string, isPinned: boolean): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();
      const pinnedBy = conversationData.pinnedBy || [];

      if (isPinned) {
        // Add user to pinnedBy array if not already present
        if (!pinnedBy.includes(userId)) {
          await updateDoc(conversationRef, {
            pinnedBy: arrayUnion(userId),
            updatedAt: serverTimestamp(),
          });
        }
      } else {
        // Remove user from pinnedBy array
        await updateDoc(conversationRef, {
          pinnedBy: arrayRemove(userId),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Failed to pin conversation:', error);
      throw new Error('Failed to pin conversation');
    }
  }

  /**
   * Add participants to a group conversation
   */
  async addParticipantsToGroup(
    conversationId: string,
    userIds: string[],
    addedBy: string
  ): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();
      
      // Verify it's a group conversation
      if (conversationData.type !== 'group') {
        throw new Error('Can only add participants to group conversations');
      }

      // Get existing participants
      const existingParticipantIds = conversationData.participantIds || [];
      const existingParticipantDetails = conversationData.participantDetails || {};

      // Filter out users who are already participants
      const newUserIds = userIds.filter(id => !existingParticipantIds.includes(id));

      if (newUserIds.length === 0) {
        throw new Error('All selected users are already participants');
      }

      // Get user details for new participants
      const newParticipantDetails: Record<string, ParticipantInfo> = {};
      const now = new Date();

      await Promise.all(
        newUserIds.map(async (userId) => {
          const user = await this.getUserDetails(userId);
          newParticipantDetails[userId] = {
            name: user.name,
            ...(user.avatar && { avatar: user.avatar }),
            role: user.role,
            addedAt: now,
            addedBy: addedBy,
          };
        })
      );

      // Update conversation
      await updateDoc(conversationRef, {
        participantIds: arrayUnion(...newUserIds),
        participantDetails: {
          ...existingParticipantDetails,
          ...newParticipantDetails,
        },
        updatedAt: serverTimestamp(),
      });

      // Send system message for each added user
      const addedByUser = await this.getUserDetails(addedBy);
      for (const userId of newUserIds) {
        const addedUser = await this.getUserDetails(userId);
        await this.sendSystemMessage(
          conversationId,
          `${addedByUser.name} added ${addedUser.name} to the group`,
          addedBy
        );
      }
    } catch (error) {
      console.error('Failed to add participants:', error);
      throw new Error('Failed to add participants to group');
    }
  }

  /**
   * Remove participant from group conversation
   */
  async removeParticipantFromGroup(
    conversationId: string,
    userIdToRemove: string,
    removedBy: string
  ): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();
      
      // Verify it's a group conversation
      if (conversationData.type !== 'group') {
        throw new Error('Can only remove participants from group conversations');
      }

      const participantIds = conversationData.participantIds || [];
      const participantDetails = conversationData.participantDetails || {};

      // Prevent removing creator
      if (conversationData.createdBy === userIdToRemove) {
        throw new Error('Cannot remove the group creator');
      }

      // Prevent removing last participant
      if (participantIds.length <= 2) {
        throw new Error('Cannot remove the last participant');
      }

      // Verify user is a participant
      if (!participantIds.includes(userIdToRemove)) {
        throw new Error('User is not a participant in this conversation');
      }

      // Get user details BEFORE removing from conversation (needed for system message)
      const removedByUser = await this.getUserDetails(removedBy);
      const removedUser = await this.getUserDetails(userIdToRemove);
      
      // Prepare system message content BEFORE removing user
      let systemMessageContent: string;
      if (userIdToRemove === removedBy) {
        // User left the group - get full name for the message
        const userDetails = await this.getUserDetailsBatch([userIdToRemove]);
        const userDetail = userDetails[userIdToRemove];
        const fullName = userDetail 
          ? `${userDetail.name} ${userDetail.surname}`.trim()
          : removedUser.name;
        systemMessageContent = `${fullName} left the group chat`;
      } else {
        // Someone was removed by another user
        systemMessageContent = `${removedByUser.name} removed ${removedUser.name} from the group`;
      }
      
      // Send system message FIRST (while user still has permission)
      await this.sendSystemMessage(
        conversationId,
        systemMessageContent,
        removedBy
      );
      
      // Prepare all updates in a single object to avoid permission issues
      // After removing from participantIds, the user loses access, so we must do everything in one update
      const updatedParticipantDetails = { ...participantDetails };
      delete updatedParticipantDetails[userIdToRemove];
      
      const unreadCount = conversationData.unreadCount || {};
      const updatedUnreadCount = { ...unreadCount };
      delete updatedUnreadCount[userIdToRemove];
      
      // Perform all updates in a single operation
      // This ensures the user still has permission (they're in resource.data.participantIds)
      await updateDoc(conversationRef, {
        participantIds: arrayRemove(userIdToRemove),
        participantDetails: updatedParticipantDetails,
        unreadCount: updatedUnreadCount,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to remove participant:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to remove participant from group');
    }
  }

  /**
   * Update group chat name
   */
  async updateConversationName(
    conversationId: string,
    newName: string,
    updatedBy: string
  ): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();

      // Verify it's a group conversation
      if (conversationData.type !== 'group') {
        throw new Error('Can only rename group conversations');
      }

      // Verify user is a participant
      const participantIds = conversationData.participantIds || [];
      if (!participantIds.includes(updatedBy)) {
        throw new Error('User is not a participant in this conversation');
      }

      // Validate name length
      if (newName.trim().length === 0) {
        throw new Error('Group name cannot be empty');
      }

      if (newName.trim().length > 100) {
        throw new Error('Group name cannot exceed 100 characters');
      }

      // Update the conversation name
      await updateDoc(conversationRef, {
        name: newName.trim(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update conversation name:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update conversation name');
    }
  }

  /**
   * Delete a conversation and all its messages
   * This permanently removes all data from the database
   */
  async deleteConversation(conversationId: string, deletedBy: string): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();

      // Verify user is a participant
      const participantIds = conversationData.participantIds || [];
      if (!participantIds.includes(deletedBy)) {
        throw new Error('User is not a participant in this conversation');
      }

      // For group chats, only the creator can delete
      if (conversationData.type === 'group' && conversationData.createdBy !== deletedBy) {
        throw new Error('Only the group creator can delete the group chat');
      }

      // Get all messages in the conversation
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      const messagesSnapshot = await getDocs(messagesRef);

      // Use batch write to delete all messages and the conversation atomically
      // Note: Firestore batch operations have a limit of 500 operations
      let batch = writeBatch(db);
      const MAX_BATCH_SIZE = 500;
      let operationCount = 0;

      // Delete all messages
      for (const messageDoc of messagesSnapshot.docs) {
        batch.delete(messageDoc.ref);
        operationCount++;
        
        // If we hit the batch limit, commit and start a new batch
        if (operationCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      // Delete the conversation (in the last batch)
      batch.delete(conversationRef);
      operationCount++;

      // Commit the final batch
      await batch.commit();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete conversation');
    }
  }

  /**
   * Send a system message to a conversation
   */
  private async sendSystemMessage(
    conversationId: string,
    content: string,
    senderId: string
  ): Promise<void> {
    try {
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      const senderDetails = await this.getUserDetails(senderId);

      // Build message data, only including avatar if it exists
      const messageData: Record<string, unknown> = {
        conversationId,
        senderId,
        senderName: senderDetails.name,
        content: content, // System messages are not encrypted
        type: 'system' as MessageType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isEdited: false,
        deletedBy: [],
        readBy: {},
        deliveredTo: {},
        reactions: {},
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)), // 2 years
      };

      // Only add senderAvatar if it exists (Firestore doesn't allow undefined)
      if (senderDetails.avatar) {
        messageData.senderAvatar = senderDetails.avatar;
      }

      await addDoc(messagesRef, messageData);
    } catch (error) {
      console.error('Failed to send system message:', error);
      // Don't throw - system messages are not critical
    }
  }

  /**
   * Update typing status
   */
  async setTyping(conversationId: string, userId: string, isTyping: boolean): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      
      if (isTyping) {
        await updateDoc(conversationRef, {
          typingUsers: arrayUnion(userId),
        });
      } else {
        await updateDoc(conversationRef, {
          typingUsers: arrayRemove(userId),
        });
      }
    } catch (error) {
      console.error('Failed to set typing status:', error);
    }
  }

  /**
   * Mark messages as read
   */
  async markAsRead(conversationId: string, messageIds: string[], userId: string): Promise<void> {
    try {
      const now = serverTimestamp();

      // Update read status for each message
      const promises = messageIds.map(messageId => {
        const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
        return updateDoc(messageRef, {
          [`readBy.${userId}`]: now,
        });
      });

      await Promise.all(promises);

      // Reset unread count for conversation
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount.${userId}`]: 0,
      });
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }

  /**
   * Add or update a reaction to a message
   */
  async reactToMessage(
    conversationId: string,
    messageId: string,
    userId: string,
    emoji: string
  ): Promise<void> {
    try {
      const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
      await updateDoc(messageRef, {
        [`reactions.${userId}`]: emoji,
      });
    } catch (error) {
      console.error('Failed to add reaction:', error);
      throw new Error('Failed to add reaction');
    }
  }

  /**
   * Remove a reaction from a message
   */
  async removeReaction(
    conversationId: string,
    messageId: string,
    userId: string
  ): Promise<void> {
    try {
      const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const reactions = messageDoc.data().reactions || {};
      if (reactions[userId]) {
        // Remove the user's reaction
        const updatedReactions = { ...reactions };
        delete updatedReactions[userId];
        
        await updateDoc(messageRef, {
          reactions: updatedReactions,
        });
      }
    } catch (error) {
      console.error('Failed to remove reaction:', error);
      throw new Error('Failed to remove reaction');
    }
  }

  /**
   * Listen to conversations in real-time
   * For municipality scope: only conversations with exact municipalityId (excludes legacy municipalityId=null
   * so external users do not see super admins or cross-municipality participants)
   */
  onConversationsSnapshot(
    userId: string,
    scope: ChatScope,
    municipalityId: string | undefined,
    callback: (conversations: Conversation[]) => void
  ): Unsubscribe {
    const conversationsRef = collection(db, 'conversations');

    if (municipalityId) {
      const qMatch = query(
        conversationsRef,
        where('participantIds', 'array-contains', userId),
        where('scope', '==', scope),
        where('municipalityId', '==', municipalityId),
        orderBy('updatedAt', 'desc')
      );

      return onSnapshot(
        qMatch,
        (snapshot: QuerySnapshot<DocumentData>) => {
          const conversations: Conversation[] = [];
          snapshot.forEach((docSnap) => {
            const conversation = this.convertToConversation(docSnap);
            if (!conversation.deletedBy.includes(userId)) {
              conversations.push(conversation);
            }
          });
          conversations.sort((a, b) => {
            const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
            const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
            return bTime - aTime;
          });
          callback(conversations);
        },
        (error: Error) => {
          console.error('Conversations listener error:', error);
          callback([]);
        }
      );
    }

    // No municipalityId: single query (super_admin or no filter)
    const q = query(
      conversationsRef,
      where('participantIds', 'array-contains', userId),
      where('scope', '==', scope),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const conversations: Conversation[] = [];
        snapshot.forEach((docSnap) => {
          const conversation = this.convertToConversation(docSnap);
          if (!conversation.deletedBy.includes(userId)) {
            conversations.push(conversation);
          }
        });
        callback(conversations);
      },
      (error: Error) => {
        console.error('Conversations listener error:', error);
        callback([]);
      }
    );
  }

  /**
   * Listen to messages in real-time
   * Optimized with:
   * - Parallel message decryption (10-25x faster)
   * - Limited to recent 50 messages (95% cost reduction, 10x faster for old conversations)
   * - Global listener tracking (prevents duplicate listeners)
   */
  onMessagesSnapshot(
    conversationId: string,
    userId: string,
    callback: (messages: Message[]) => void
  ): Unsubscribe {
    const listenerKey = `${conversationId}_${userId}`;
    
    // Check if listener already exists for this conversation - reuse to prevent duplicates
    if (this.activeListeners.has(listenerKey)) {
      return this.activeListeners.get(listenerKey)!;
    }
    
    const messagesRef = collection(db, `conversations/${conversationId}/messages`);
    
    // Limit to 50 most recent messages to optimize performance and costs
    // Older messages can be loaded on demand via getMessages()
    const q = query(
      messagesRef, 
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const messagePromises = snapshot.docs.map((docSnap: QueryDocumentSnapshot<DocumentData>) =>
            this.convertToMessage(docSnap)
          );
          const allMessages = await Promise.all(messagePromises);
          
          // Filter out messages deleted by this user
          const visibleMessages = allMessages.filter(
            message => !message.deletedBy.includes(userId)
          );

          // Reverse to get chronological order (oldest first)
          callback(visibleMessages.reverse());
        } catch (error) {
          console.error('Failed to process messages snapshot:', error);
          callback([]);
        }
      },
      (error: { code?: string; message?: string }) => {
        const isPermissionError = error.code === 'permission-denied' || 
                                  (error.message && error.message.includes('permission'));
        
        if (isPermissionError) {
          // User no longer has access - silently unsubscribe
          // This is expected when a user removes themselves from a group chat
          this.activeListeners.delete(listenerKey);
          callback([]);
        } else {
          // Other errors should be logged
          console.error('Messages listener error:', error);
          this.activeListeners.delete(listenerKey);
          callback([]);
        }
      }
    );
    
    // Store listener and return wrapped unsubscribe
    const wrappedUnsubscribe = () => {
      this.activeListeners.delete(listenerKey);
      unsubscribe();
    };
    
    this.activeListeners.set(listenerKey, wrappedUnsubscribe);
    return wrappedUnsubscribe;
  }

  /**
   * Update conversation last message and increment unread count
   * Optimized: Receives participantIds from caller to avoid extra read
   * Uses atomic increment to prevent race conditions with rapid messages
   */
  private async updateConversationLastMessage(
    conversationId: string,
    participantIds: string[],
    lastMessage: { content: string; senderId: string; senderName: string; timestamp: Date }
  ): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      
      // Filter to get other participants (exclude message sender)
      const otherParticipants = participantIds.filter(
        (id: string) => id !== lastMessage.senderId
      );
      
      // Use Firestore increment() for atomic updates - prevents race conditions
      const unreadUpdates: Record<string, FieldValue> = {};
      otherParticipants.forEach((participantId: string) => {
        unreadUpdates[`unreadCount.${participantId}`] = increment(1);
      });
      
      // Single atomic update (no read required)
      await updateDoc(conversationRef, {
        lastMessage,
        updatedAt: serverTimestamp(),
        ...unreadUpdates,
      });
    } catch (error) {
      console.error('Failed to update last message:', error);
    }
  }

  /**
   * Convert Firestore document to Conversation
   */
  private convertToConversation(doc: QueryDocumentSnapshot<DocumentData>): Conversation {
    const data = doc.data();
    return {
      id: doc.id,
      type: data.type,
      scope: data.scope,
      municipalityId: data.municipalityId,
      name: data.name,
      description: data.description,
      avatar: data.avatar,
      participantIds: data.participantIds || [],
      participantDetails: data.participantDetails || {},
      createdBy: data.createdBy,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate?.() || data.lastMessage.timestamp,
      } : undefined,
      unreadCount: data.unreadCount || {},
      typingUsers: data.typingUsers || [],
      isArchived: data.isArchived || {},
      isMuted: data.isMuted || {},
      pinnedBy: data.pinnedBy || [],
      deletedBy: data.deletedBy || [],
    };
  }

  /**
   * Convert Firestore document to Message (with decryption)
   */
  private async convertToMessage(doc: QueryDocumentSnapshot<DocumentData>): Promise<Message> {
    const data = doc.data();
    
    // Decrypt message content (skip for system messages - they're plain text)
    let decryptedContent = data.content;
    if (data.type !== 'system') {
      const version = (data.encryptionKeyVersion === 'v1' || data.encryptionKeyVersion === 'v2')
        ? data.encryptionKeyVersion
        : 'v1'; // Legacy messages without field default to v1
      try {
        decryptedContent = await encryptionService.decryptMessage(data.content, version);
      } catch (error) {
        console.error('Failed to decrypt message:', error);
        decryptedContent = '[Unable to decrypt message]';
      }
    }
    // System messages are already plain text, no decryption needed

    return {
      id: doc.id,
      conversationId: data.conversationId,
      senderId: data.senderId,
      senderName: data.senderName,
      senderAvatar: data.senderAvatar,
      content: decryptedContent,
      type: data.type,
      metadata: data.metadata,
      encryptionKeyVersion: (data.encryptionKeyVersion === 'v1' || data.encryptionKeyVersion === 'v2')
        ? data.encryptionKeyVersion
        : 'v1',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      editedAt: data.editedAt?.toDate(),
      isEdited: data.isEdited || false,
      deletedBy: data.deletedBy || [],
      readBy: this.convertTimestampMap(data.readBy),
      deliveredTo: this.convertTimestampMap(data.deliveredTo),
      reactions: data.reactions || {},
      expiresAt: data.expiresAt?.toDate() || new Date(),
    };
  }

  /**
   * Convert Firestore timestamp map to Date map
   */
  private convertTimestampMap(timestampMap: Record<string, unknown>): Record<string, Date> {
    const result: Record<string, Date> = {};
    
    if (timestampMap) {
      Object.entries(timestampMap).forEach(([key, value]) => {
        const timestampValue = value as { toDate?: () => Date } | Date;
        result[key] = timestampValue && typeof timestampValue === 'object' && 'toDate' in timestampValue && timestampValue.toDate 
          ? timestampValue.toDate() 
          : timestampValue as Date;
      });
    }
    
    return result;
  }

  /**
   * Get available users for chat (scope-aware)
   *
   * Municipality scope: returns ONLY (1) municipality staff (municipalityId === municipalityId)
   * and (2) invited external users (role super_admin with assignedMunicipalityIds containing municipalityId).
   * Excludes top-level super admins and users from other municipalities.
   */
  async getAvailableUsers(
    currentUserId: string,
    scope: ChatScope,
    municipalityId?: string
  ): Promise<User[]> {
    try {
      const usersRef = collection(db, 'users');

      const toDate = (timestamp: unknown): Date => {
        if (!timestamp) return new Date();
        if (timestamp instanceof Date) return timestamp;
        if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
          return (timestamp as { toDate: () => Date }).toDate();
        }
        return new Date();
      };

      const toUser = (docSnap: QueryDocumentSnapshot<DocumentData>): User | null => {
        if (docSnap.id === currentUserId) return null;
        const userData = docSnap.data();
        return {
          id: docSnap.id,
          email: userData.email,
          displayName: userData.displayName || userData.name,
          photoURL: userData.photoURL,
          role: userData.role,
          createdAt: toDate(userData.createdAt),
          updatedAt: toDate(userData.updatedAt),
        };
      };

      if (scope === 'super_admin') {
        const q = query(
          usersRef,
          where('role', '==', 'super_admin')
        );
        const snapshot = await getDocs(q);
        const users: User[] = [];
        snapshot.forEach((docSnap) => {
          const u = toUser(docSnap);
          if (u) users.push(u);
        });
        return users;
      }

      // Municipality scope: municipality staff + invited external users (assigned to this municipality only)
      if (!municipalityId) return [];

      const [snapshotMunicipality, snapshotAssigned] = await Promise.all([
        getDocs(
          query(
            usersRef,
            where('municipalityId', '==', municipalityId)
          )
        ),
        getDocs(
          query(
            usersRef,
            where('role', '==', 'super_admin'),
            where('assignedMunicipalityIds', 'array-contains', municipalityId)
          )
        ),
      ]);

      const seenIds = new Set<string>();
      const users: User[] = [];

      const addUser = (docSnap: QueryDocumentSnapshot<DocumentData>) => {
        if (seenIds.has(docSnap.id)) return;
        const u = toUser(docSnap);
        if (u) {
          seenIds.add(docSnap.id);
          users.push(u);
        }
      };

      snapshotMunicipality.forEach(addUser);
      snapshotAssigned.forEach(addUser);

      return users;
    } catch (error) {
      console.error('Failed to get available users:', error);
      return [];
    }
  }

  /**
   * Search messages across conversations with filters
   * 
   * @param userId - Current user ID
   * @param searchQuery - Text to search for in message content
   * @param filters - Search filters (date range, sender, conversation type, scope)
   * @param limit - Maximum number of results (default: 50)
   * @returns Array of messages matching the search criteria
   */
  async searchMessages(
    userId: string,
    searchQuery: string,
    filters: {
      dateFrom?: Date;
      dateTo?: Date;
      senderId?: string;
      conversationType?: 'direct' | 'group';
      scope?: ChatScope;
      municipalityId?: string;
      conversationIds?: string[]; // Limit search to specific conversations
    },
    maxResults: number = 50
  ): Promise<Array<Message & { conversationName?: string; conversationType?: 'direct' | 'group' }>> {
    try {
      // Get user's conversations first (scope-aware)
      const conversations = await this.getConversations(
        userId,
        filters.scope || 'municipality',
        filters.municipalityId
      );

      // Filter conversations by type if specified
      let filteredConversations = conversations;
      if (filters.conversationType) {
        filteredConversations = conversations.filter(
          (conv) => conv.type === filters.conversationType
        );
      }

      // Filter by conversation IDs if specified
      if (filters.conversationIds && filters.conversationIds.length > 0) {
        filteredConversations = filteredConversations.filter((conv) =>
          filters.conversationIds!.includes(conv.id)
        );
      }

      // If no conversations match, return empty array
      if (filteredConversations.length === 0) {
        return [];
      }

      // Search messages in each conversation
      const searchPromises = filteredConversations.map(async (conversation) => {
        try {
          const messagesRef = collection(db, `conversations/${conversation.id}/messages`);
          let q = query(messagesRef);

          // Apply date filters
          if (filters.dateFrom) {
            q = query(q, where('createdAt', '>=', Timestamp.fromDate(filters.dateFrom)));
          }
          if (filters.dateTo) {
            // Add 1 day to include the entire end date
            const endDate = new Date(filters.dateTo);
            endDate.setHours(23, 59, 59, 999);
            q = query(q, where('createdAt', '<=', Timestamp.fromDate(endDate)));
          }

          // Apply sender filter
          if (filters.senderId) {
            q = query(q, where('senderId', '==', filters.senderId));
          }

          // Order by creation date (newest first)
          q = query(q, orderBy('createdAt', 'desc'));

          // Limit per conversation to avoid loading too many messages
          q = query(q, limit(maxResults));

          const messagesSnapshot = await getDocs(q);
          const messages: Array<Message & { conversationName?: string; conversationType?: 'direct' | 'group' }> = [];

          for (const doc of messagesSnapshot.docs) {
            try {
              const message = await this.convertToMessage(doc as QueryDocumentSnapshot<DocumentData>);

              // Filter out deleted messages
              if (message.deletedBy.includes(userId)) {
                continue;
              }

              // Filter out system messages (unless explicitly searching for them)
              // System messages are usually not searchable
              if (message.type === 'system') {
                continue;
              }

              // Apply text search filter (case-insensitive)
              if (searchQuery.trim()) {
                const contentLower = message.content.toLowerCase();
                const queryLower = searchQuery.toLowerCase();
                if (!contentLower.includes(queryLower)) {
                  continue;
                }
              }

              // Add conversation metadata
              messages.push({
                ...message,
                conversationName: conversation.name || this.getConversationDisplayName(conversation, userId),
                conversationType: conversation.type,
              });
            } catch (error) {
              console.error(`Error converting message ${doc.id}:`, error);
              // Skip messages that fail to convert (e.g., decryption errors)
              continue;
            }
          }

          return messages;
        } catch (error) {
          console.error(`Error searching messages in conversation ${conversation.id}:`, error);
          return [];
        }
      });

      // Wait for all searches to complete
      const results = await Promise.all(searchPromises);

      // Flatten and sort results by date (newest first)
      const allMessages = results.flat().sort((a, b) => {
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      // Apply global limit
      return allMessages.slice(0, maxResults);
    } catch (error) {
      console.error('Failed to search messages:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to search messages');
    }
  }

  /**
   * Get conversation display name (helper for search results)
   */
  private getConversationDisplayName(conversation: Conversation, userId: string): string {
    if (conversation.name) {
      return conversation.name;
    }

    if (conversation.type === 'direct') {
      const otherParticipant = Object.values(conversation.participantDetails).find(
        (p: ParticipantInfo) => p?.name && !conversation.participantIds.includes(userId)
      ) as ParticipantInfo | undefined;
      return otherParticipant?.name || 'Direct Chat';
    }

    return 'Group Chat';
  }
}

// Export singleton instance
export const chatService = new ChatService();

